import { MarkerType, Position, type Connection, type Edge } from "@xyflow/react";

export type U3141GraphConcordanceHandle = "top" | "bottom";

export function u3141NormalizeGraphConcordanceHandle(handle: string | null | undefined): U3141GraphConcordanceHandle | null {
  if (handle == null || handle === "") return null;
  const lower = handle.toLowerCase();
  if (lower === "top" || lower.includes("top")) return "top";
  if (lower === "bottom" || lower.includes("bottom")) return "bottom";
  return null;
}

export function u3141IsAllowedGraphConcordanceConnection(
  sourceHandle: string | null | undefined,
  targetHandle: string | null | undefined,
): boolean {
  const sh = u3141NormalizeGraphConcordanceHandle(sourceHandle);
  const th = u3141NormalizeGraphConcordanceHandle(targetHandle);
  if (!sh || !th) return false;
  return (sh === "top" && th === "bottom") || (sh === "bottom" && th === "top");
}

export function u3141ResolveGraphConcordanceLinkFromConnection(connection: Connection): {
  concordanceHolderChildLotChainId: string;
  concordanceOriginChildLotChainId: string;
} | null {
  if (!connection.source || !connection.target) return null;
  if (connection.source === connection.target) return null;
  if (!u3141IsAllowedGraphConcordanceConnection(connection.sourceHandle, connection.targetHandle)) {
    return null;
  }
  const sh = u3141NormalizeGraphConcordanceHandle(connection.sourceHandle);
  const th = u3141NormalizeGraphConcordanceHandle(connection.targetHandle);
  if (sh === "top" && th === "bottom") {
    return {
      concordanceHolderChildLotChainId: connection.target,
      concordanceOriginChildLotChainId: connection.source,
    };
  }
  if (sh === "bottom" && th === "top") {
    return {
      concordanceHolderChildLotChainId: connection.source,
      concordanceOriginChildLotChainId: connection.target,
    };
  }
  return null;
}

export function u3141GraphConcordanceEdgeId(holderChildId: string, originChildId: string): string {
  return `concordance:${holderChildId}:${originChildId}`;
}

/**
 * Rendu graphe historique cadastral : parent/citer (**holder**, ex. 1 802 263) au-dessus,
 * enfant concordance (**origin**, ex. 2-29) en dessous — trait bas du parent → haut de l’enfant,
 * **une seule** flèche sur l’embout **bas** du parent (holder), vers l’enfant.
 */
export function u3141BuildGraphConcordanceEdge(
  holderChildId: string,
  originChildId: string,
  stroke = "#171717",
): Edge {
  return {
    id: u3141GraphConcordanceEdgeId(holderChildId, originChildId),
    source: holderChildId,
    target: originChildId,
    sourceHandle: "bottom",
    targetHandle: "top",
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    /** markerStart = embout bas du parent ; orient xyflow par défaut (auto-start-reverse) */
    markerStart: {
      type: MarkerType.ArrowClosed,
      color: stroke,
      width: 22,
      height: 22,
    },
    style: { stroke, strokeWidth: 2 },
    selectable: true,
    focusable: true,
    data: {
      u3141GraphConcordance: {
        concordanceHolderChildLotChainId: holderChildId,
        concordanceOriginChildLotChainId: originChildId,
      },
    },
  };
}

export interface U3141GraphConcordanceEdgeData {
  u3141GraphConcordance?: {
    concordanceHolderChildLotChainId: string;
    concordanceOriginChildLotChainId: string;
  };
}
